import React from 'react';

export interface BlockRendererProps {
  block: { settings: Record<string, any> };
  config: {
    anaRenk: string;
    aksanRenk: string;
    font: string;
    fontBoyutu: number;
    odemeKosullari?: string;
    gecerlilikNotu?: string;
    altBilgiNotu?: string;
  };
  offer?: any;
  offerType?: 'given' | 'received';
  companyInfo?: any;
}

const formatCurrency = (amount: number | undefined, currency: string = 'TRY'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ItemsTableBlockRenderer: React.FC<BlockRendererProps> = ({ block, config, offer }) => {
  const s = block?.settings ?? {};
  const zebraStripes = s.zebraStripes !== false;
  const headerBgColor = typeof s.headerBgColor === 'string' && s.headerBgColor ? s.headerBgColor : config.anaRenk;
  const showDiscountSetting = s.showDiscount === true;
  const showSctSetting = s.showSct === true;

  const items = Array.isArray(offer?.items) ? offer.items : [];
  const currency = offer?.currency != null ? String(offer.currency) : 'TRY';

  const anyDiscount = items.some(
    (it: any) => typeof it?.discountRate === 'number' && !isNaN(it.discountRate) && it.discountRate > 0
  );
  const anySct = items.some((it: any) => typeof it?.sctRate === 'number' && !isNaN(it.sctRate) && it.sctRate > 0);

  const showDiscountCol = showDiscountSetting && anyDiscount;
  const showSctCol = showSctSetting && anySct;

  const fs = `${Math.max(config.fontBoyutu - 1, 10)}px`;
  const thStyle: React.CSSProperties = {
    backgroundColor: headerBgColor,
    color: '#fff',
    padding: '8px 6px',
    fontSize: fs,
    fontWeight: 600,
    textAlign: 'left' as const,
    borderBottom: `2px solid ${config.aksanRenk}`,
  };

  const tdStyle: React.CSSProperties = {
    padding: '7px 6px',
    fontSize: fs,
    borderBottom: '1px solid #eee',
    verticalAlign: 'top' as const,
  };

  let zebraBg = '#f9f9f9';
  try {
    zebraBg = hexToRgba(config.aksanRenk, 0.04);
  } catch {
    zebraBg = '#f9f9f9';
  }

  const lineTotal = (it: any): number | undefined => {
    if (typeof it?.totalPrice === 'number') return it.totalPrice;
    if (typeof it?.grossTotal === 'number') return it.grossTotal;
    return undefined;
  };

  const itemName = (it: any): string => {
    const n = it?.name ?? it?.itemName;
    return n != null ? String(n) : '-';
  };

  const colSpan = 7 + (showDiscountCol ? 1 : 0) + (showSctCol ? 1 : 0);

  return (
    <div style={{ width: '100%', overflowX: 'auto' as const }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse' as const,
          fontFamily: `'${config.font}', Arial, sans-serif`,
        }}
      >
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '32px' }}>#</th>
            <th style={thStyle}>Mal/Hizmet</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
            <th style={thStyle}>Birim</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Birim Fiyat</th>
            {showDiscountCol ? (
              <th style={{ ...thStyle, textAlign: 'right' }}>İsk.%</th>
            ) : null}
            <th style={{ ...thStyle, textAlign: 'right' }}>KDV%</th>
            {showSctCol ? (
              <th style={{ ...thStyle, textAlign: 'right' }}>ÖTV%</th>
            ) : null}
            <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={colSpan} style={{ ...tdStyle, textAlign: 'center', color: '#888' }}>
                Satır bulunmuyor
              </td>
            </tr>
          ) : (
            items.map((it: any, idx: number) => {
              const qty = typeof it?.quantity === 'number' ? it.quantity : '-';
              const unit = it?.unit != null ? String(it.unit) : '-';
              const unitPrice = typeof it?.unitPrice === 'number' ? it.unitPrice : undefined;
              const vat = typeof it?.vatRate === 'number' ? it.vatRate : '-';
              const disc = typeof it?.discountRate === 'number' ? it.discountRate : '-';
              const sct = typeof it?.sctRate === 'number' ? it.sctRate : '-';
              const bg = zebraStripes && idx % 2 === 1 ? zebraBg : '#fff';
              return (
                <tr key={it?.id != null ? String(it.id) : `row-${idx}`} style={{ backgroundColor: bg }}>
                  <td style={tdStyle}>{idx + 1}</td>
                  <td style={tdStyle}>{itemName(it)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{qty}</td>
                  <td style={tdStyle}>{unit}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(unitPrice, currency)}</td>
                  {showDiscountCol ? (
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{disc === '-' ? '-' : `${disc}`}</td>
                  ) : null}
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{vat === '-' ? '-' : `${vat}`}</td>
                  {showSctCol ? (
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{sct === '-' ? '-' : `${sct}`}</td>
                  ) : null}
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(lineTotal(it), currency)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ItemsTableBlockRenderer;
